import { Formik } from 'formik'
import { Grid } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { boolean, number, object, string } from 'yup'

import { getIsSubscribed } from '../../api/api'
import BillingInfo from './BillingInfo'
import Coupon from './Coupon'
import Credit from './Credit'
import DeliveryInstructions from './DeliveryInstructions'
import Gifts from './Gift'
import Subscription from './Subscription'
import UserInfo from './UserInfo'

const phoneRegExp = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/

const CheckoutForm = ({
  applyCoupon,
  applyCredit,
  availableCredits,
  checkoutCart,
  checkoutError,
  checkoutStart,
  coupon,
  couponError,
  credit,
  creditCards,
  creditCardsError,
  creditError,
  curSavedLocation,
  dropoffId,
  isAvailableCreditsLoading,
  isCheckoutCartLoading,
  isCouponApplying,
  isCreditApplying,
  loadCheckoutCartStart,
  program,
  selectedCreditCard,
  storedCurrencyUsage,
  subscriptionDetails,
  subscriptionPromoPreference,
  suggestedCredits,
  toggleDeliveryInstructionsDialog,
  user,
}) => {
  const [isSubscribed, setIsSubscribed] = useState(null)

  useEffect(() => {
    async function fetchData() {
      const res = await getIsSubscribed()
      setIsSubscribed(res)
    }
    fetchData()
  }, [])

  const now = new Date()

  const validateExpirationDate = value => {
    if (!value) {
      return false
    }
    const cardMonth = parseInt(value.slice(0, 2))
    const cardYear = parseInt('20' + value.slice(3))
    const currentMonth = now.getMonth() + 1
    const currentYear = now.getFullYear()

    if (currentYear > cardYear) {
      return false
    }
    if (currentYear === cardYear && currentMonth > cardMonth) {
      return false
    }
    return true
  }

  const validateExpirationMonth = value => {
    if (!value) {
      return false
    }

    return parseInt(value.slice(0, 2)) < 13 && parseInt(value.slice(0, 2)) > 0
  }

  const schema = object().shape({
    SelectedCard: number()
      .nullable()
      .test('test', 'Please select a credit card.', val => {
        if (checkoutCart.orderTotal === 0) {
          return true
        } else return val != null
      }),
    User: object().shape({
      firstName: string().required('First Name is required.'),
      lastName: string().required('Last Name is required.'),
      phone: string()
        .required('Phone Number is required.')
        .matches(phoneRegExp, 'Phone Number must be 10 digits.'),
    }),
    cc: object().when('SelectedCard', {
      is: val => val === -1 && checkoutCart.orderTotal !== 0,
      then: s =>
        s.shape({
          cvv: string()
            .required('CVV is required.')
            .trim()
            .min(3, 'CVV is too short.'),
          exp: string()
            .required('Expiration is required')
            .trim()
            .min(5, 'Expiration too short')
            .test('valid-month', 'Invalid month', value => validateExpirationMonth(value))
            .test('valid-exp', 'Date must be in future', value => validateExpirationDate(value)),
          firstName: string().required('First Name is required.'),
          lastName: string().required('Last Name is required.'),
          number: string()
            .required('Credit Card Number is required.')
            .trim()
            .min(16, 'Credit Card Number is too short.'),
          zip: string()
            .required('Zip Code is required.')
            .min(5, 'Zip Code must be 5 digits long.'),
        }),
    }),
    isSubscribed: boolean().when('saveCard', {
      is: value => value === false,
      otherwise: boolean().notRequired(),
      then: boolean()
        .required()
        .oneOf([false], 'A credit card on file is required. Please add and save your card.'),
    }),
  })

  return (
    <Formik
      initialValues={{
        SelectedCard: creditCards.length > 0 ? null : -1,
        User: {
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone,
        },
        cc: {
          cvv: '',
          exp: '',
          firstName: '',
          lastName: '',
          number: '',
          zip: '',
        },
        isSubscribed: false,
        saveCard: true,
      }}
      onSubmit={values => {
        checkoutStart(values)
      }}
      validationSchema={schema}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldTouched,
        setFieldValue,
        touched,
        values,
      }) => (
        <form className="Form" onSubmit={handleSubmit}>
          <section className="Form-section">
            <h5 className="TextAlt Form-heading">Customer Information</h5>
            <UserInfo
              email={user.email}
              errors={errors}
              handleBlur={handleBlur}
              handleChange={handleChange}
              touched={touched}
              user={{
                ...user,
              }}
              values={values}
            />
            {curSavedLocation?.deliveryInstructionsSupported && (
              <DeliveryInstructions
                curSavedLocation={curSavedLocation}
                toggleDeliveryInstructionsDialog={toggleDeliveryInstructionsDialog}
              />
            )}
          </section>
          <section className="Form-section">
            <h5 className="TextAlt Form-heading">Discounts</h5>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {((subscriptionDetails?.subscriptionFee !== 0 &&
                  checkoutCart.orderTotal >= 1.99 &&
                  subscriptionDetails?.isSubscriptionEnabled === true) ||
                  isSubscribed === true) && (
                  <Subscription
                    dropoffId={dropoffId}
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    storedCurrencyUsage={storedCurrencyUsage}
                    subscribe={values.subscribe}
                    subscriptionFee={checkoutCart.subscriptionFee}
                    subscriptionPromoPreference={subscriptionPromoPreference}
                    values={values}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Coupon
                  applyCoupon={applyCoupon}
                  couponAmount={coupon ? coupon.couponAmount : 0}
                  couponApplied={coupon ? coupon.couponApplied : false}
                  couponCode={coupon ? coupon.couponCode : null}
                  error={couponError}
                  isCheckoutCartLoading={isCheckoutCartLoading}
                  isCouponApplying={isCouponApplying}
                  isDailyLink={coupon ? coupon.isDailyLink : false}
                />
              </Grid>
              <Grid item xs={12}>
                {checkoutCart.orderItems &&
                  checkoutCart.orderItems.length > 0 &&
                  (!credit || credit.creditBalance === 0 || (
                    <Credit
                      applyCredit={applyCredit}
                      availableCredits={availableCredits}
                      creditsApplied={credit ? credit.creditsApplied : false}
                      creditsUsed={credit ? credit.creditBalanceUsed : 0}
                      error={creditError}
                      isAvailableCreditsLoading={isAvailableCreditsLoading}
                      isCheckoutCartLoading={isCheckoutCartLoading}
                      isCreditApplying={isCreditApplying}
                      suggestedCredits={suggestedCredits}
                    />
                  ))}
              </Grid>
              {program && program !== '' && (
                <Grid item xs={12}>
                  <EppInfo program={program} />
                </Grid>
              )}
            </Grid>
          </section>
          <section className="Form-section">
            <h5 className="TextAlt Form-heading" key="title">
              Billing Information
            </h5>
            {storedCurrencyUsage?.storedCurrencyBalance > 0 && (
              <Gifts
                dropoffId={dropoffId}
                isSubscribed={isSubscribed}
                loadCheckoutCartStart={loadCheckoutCartStart}
                orderTotal={checkoutCart?.orderTotal}
                storedCurrencyUsage={storedCurrencyUsage}
              />
            )}
            {checkoutCart && checkoutCart.orderTotal === 0 ? (
              <h3>Your meal is covered!</h3>
            ) : (
              <BillingInfo
                creditCards={creditCards}
                creditCardsError={creditCardsError}
                errors={errors}
                handleBlur={handleBlur}
                handleChange={handleChange}
                key="billing-form"
                selectedCreditCard={selectedCreditCard}
                setFieldTouched={setFieldTouched}
                setFieldValue={setFieldValue}
                touched={touched}
                values={values}
              />
            )}
            {checkoutError && (
              <div className="primary">
                <p>{checkoutError.message}</p>
              </div>
            )}
          </section>
          <button className="hidden" id="checkout" style={{ visibility: 'hidden' }} type="submit" />
        </form>
      )}
    </Formik>
  )
}

export const EppInfo = ({ program }) => {
  const programs = {
    DAILY: 'today',
    MONTHLY: 'this month',
    WEEKLY: 'this week',
  }
  return (
    <div className="Input is-inactive">
      <label htmlFor="program">{program.programName}</label>
      <p>
        ${(program.discountAmountInPennies / 100).toFixed(2)} of $
        {(program.originalDiscountAmountInPennies / 100).toFixed(2)} remaining{' '}
        {programs[program.programUsageType]}
      </p>
    </div>
  )
}

CheckoutForm.defaultProps = {
  user: {},
}
export default CheckoutForm
